import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Save, Plus, Pencil, Trash2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { LogoUploader } from '../components/LogoUploader';

export default function SettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({ company_name: '', company_gst: '', company_address: '', company_pan: '', company_phone: '', company_email: '', bank_details: '', whatsapp_enabled: false, whatsapp_api_key: '', rent_property_name: '', upi_id: '' });
  const [users, setUsers] = useState([]);
  const [userDialog, setUserDialog] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'user', permissions: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [setRes, usrRes] = await Promise.all([api.get('/settings'), api.get('/users')]);
        setSettings(setRes.data);
        setUsers(usrRes.data);
      } catch (err) {
        console.error(err);
      } finally { setLoading(false); }
    };
    fetch();
  }, []);

  const saveSettings = async () => {
    try {
      await api.put('/settings', settings);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save settings'); }
  };

  const openNewUser = () => {
    setEditUserId(null);
    setUserForm({ name: '', email: '', password: '', role: 'user', permissions: [] });
    setUserDialog(true);
  };

  const openEditUser = (u) => {
    setEditUserId(u._id);
    setUserForm({ name: u.name, email: u.email, password: '', role: u.role, permissions: u.permissions || [] });
    setUserDialog(true);
  };

  const saveUser = async () => {
    if (!userForm.name || !userForm.email) { toast.error('Name and email required'); return; }
    try {
      if (editUserId) {
        await api.put(`/users/${editUserId}`, userForm);
        toast.success('User updated');
      } else {
        if (!userForm.password) { toast.error('Password required for new user'); return; }
        await api.post('/users', userForm);
        toast.success('User created');
      }
      setUserDialog(false);
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success('User deleted');
      const { data } = await api.get('/users');
      setUsers(data);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="settings-page">
      <div className="flex items-center gap-3 mb-6">
        <Shield size={28} className="text-[#D4AF37]" />
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Settings</h1>
          <p className="text-sm text-[#4F734F] mt-1">Admin configuration panel</p>
        </div>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="invoice" data-testid="tab-invoice">Invoice Settings</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">Logos</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">User Management</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <div className="bg-white border border-[#E6EDE6] rounded-sm p-6 max-w-2xl space-y-5">
            <h2 className="font-heading text-lg font-medium text-[#213021]">Company Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Company Name</Label>
                <Input value={settings.company_name} onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))} className="input-field" placeholder="SVT & Co" data-testid="company-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Company GSTIN</Label>
                <Input value={settings.company_gst || ''} onChange={(e) => setSettings(prev => ({ ...prev, company_gst: e.target.value }))} className="input-field font-mono" placeholder="e.g. 29AABCT1332L1ZH" data-testid="company-gst-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Company PAN</Label>
                <Input value={settings.company_pan || ''} onChange={(e) => setSettings(prev => ({ ...prev, company_pan: e.target.value }))} className="input-field font-mono" placeholder="e.g. AABCT1332L" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Phone Number</Label>
                <Input value={settings.company_phone || ''} onChange={(e) => setSettings(prev => ({ ...prev, company_phone: e.target.value }))} className="input-field" placeholder="+91 98765 43210" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Email</Label>
                <Input value={settings.company_email || ''} onChange={(e) => setSettings(prev => ({ ...prev, company_email: e.target.value }))} className="input-field" placeholder="info@svtandco.com" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Company Address</Label>
              <Input value={settings.company_address || ''} onChange={(e) => setSettings(prev => ({ ...prev, company_address: e.target.value }))} className="input-field" placeholder="Full company address for invoices" data-testid="company-address-input" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Rent Property Name</Label>
                <Input value={settings.rent_property_name} onChange={(e) => setSettings(prev => ({ ...prev, rent_property_name: e.target.value }))} className="input-field" placeholder="Adishakthi Royal Grove" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Default UPI ID</Label>
                <Input value={settings.upi_id} onChange={(e) => setSettings(prev => ({ ...prev, upi_id: e.target.value }))} className="input-field" placeholder="your@upi" />
              </div>
            </div>
            <Button onClick={saveSettings} className="btn-primary gap-2" data-testid="save-settings-btn"><Save size={16} /> Save Settings</Button>
          </div>
        </TabsContent>

        <TabsContent value="invoice">
          <div className="bg-white border border-[#E6EDE6] rounded-sm p-6 max-w-2xl space-y-5">
            <h2 className="font-heading text-lg font-medium text-[#213021]">Invoice Configuration</h2>
            <p className="text-xs text-olive-500">These details appear on every invoice PDF generated from the system.</p>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Company Name (on invoice)</Label>
              <Input value={settings.company_name} onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))} className="input-field" placeholder="SVT & Co" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">GSTIN (on invoice)</Label>
              <Input value={settings.company_gst || ''} onChange={(e) => setSettings(prev => ({ ...prev, company_gst: e.target.value }))} className="input-field font-mono" placeholder="29AABCT1332L1ZH" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Company Address (on invoice)</Label>
              <Input value={settings.company_address || ''} onChange={(e) => setSettings(prev => ({ ...prev, company_address: e.target.value }))} className="input-field" placeholder="Near Adhishakthi Royal Grove, Whitefield, Bengaluru 560066" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Bank Details (on invoice)</Label>
              <textarea value={settings.bank_details || ''} onChange={(e) => setSettings(prev => ({ ...prev, bank_details: e.target.value }))} className="input-field min-h-[100px]" placeholder={"Account Name: SVT & Co\nA/C No: 1234567890\nIFSC: SBIN0001234\nBank: State Bank of India\nBranch: Whitefield"} data-testid="bank-details-input" />
            </div>
            <div className="bg-[#F4F7F4] border border-[#E6EDE6] rounded-sm p-4">
              <h3 className="text-xs uppercase font-semibold text-[#4F734F] mb-2">Preview</h3>
              <div className="text-sm space-y-1">
                <p className="font-semibold text-[#111B11]">{settings.company_name || 'Company Name'}</p>
                <p className="text-olive-500 text-xs">{settings.company_address || 'Company Address'}</p>
                {settings.company_gst && <p className="text-olive-500 text-xs">GSTIN: {settings.company_gst}</p>}
                {settings.bank_details && <p className="text-olive-500 text-xs whitespace-pre-line mt-2 pt-2 border-t border-[#E6EDE6]">Bank: {settings.bank_details}</p>}
              </div>
            </div>
            <Button onClick={saveSettings} className="btn-primary gap-2" data-testid="save-invoice-settings-btn"><Save size={16} /> Save Invoice Settings</Button>
          </div>
        </TabsContent>

        <TabsContent value="branding">
          <div className="bg-white border border-[#E6EDE6] rounded-sm p-6 max-w-2xl space-y-5" data-testid="branding-section">
            <div>
              <h2 className="font-heading text-lg font-medium text-[#213021]">Brand Logos</h2>
              <p className="text-xs text-olive-500 mt-1">Changes apply immediately. PNG with a transparent background works best (max 5 MB).</p>
            </div>
            <LogoUploader
              kind="group"
              title="SVT Aaventures Logo (Main Page)"
              hint="Shown in the header and footer of the main group landing page."
              info={settings.group_logo}
              onChange={(v) => setSettings(prev => ({ ...prev, group_logo: v }))}
            />
            <LogoUploader
              kind="company"
              title="SVT & Co Logo (Invoices & Website)"
              hint="Used on Invoice and Purchase Order PDFs and the SVT & Co website header and footer."
              info={settings.company_logo}
              fallback="/images/logo.png"
              dark
              onChange={(v) => setSettings(prev => ({ ...prev, company_logo: v }))}
            />
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="mb-4 flex justify-end">
            <Button onClick={openNewUser} className="btn-accent gap-2" data-testid="add-user-btn"><Plus size={16} /> Add User</Button>
          </div>
          <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
            <table className="data-table w-full">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Created</th><th>Actions</th></tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id}>
                    <td className="font-medium text-[#111B11]">{u.name}</td>
                    <td>{u.email}</td>
                    <td><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${u.role === 'admin' ? 'bg-[#D4AF37]/20 text-[#8B7500]' : 'bg-olive-100 text-olive-700'}`}>{u.role}</span></td>
                    <td>{u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '-'}</td>
                    <td>
                      <div className="flex gap-1">
                        <button onClick={() => openEditUser(u)} className="p-1.5 hover:bg-olive-50 rounded-sm"><Pencil size={14} className="text-olive-500" /></button>
                        {u._id !== user?.id && <button onClick={() => deleteUser(u._id)} className="p-1.5 hover:bg-red-50 rounded-sm"><Trash2 size={14} className="text-red-500" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="integrations">
          <div className="bg-white border border-[#E6EDE6] rounded-sm p-6 max-w-lg space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-[#213021]">WhatsApp Integration</p>
                <p className="text-xs text-olive-500">Send automated reminders via WhatsApp</p>
              </div>
              <Switch checked={settings.whatsapp_enabled} onCheckedChange={(v) => setSettings(prev => ({ ...prev, whatsapp_enabled: v }))} data-testid="whatsapp-toggle" />
            </div>
            {settings.whatsapp_enabled && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">WhatsApp API Key</Label>
                <Input type="password" value={settings.whatsapp_api_key} onChange={(e) => setSettings(prev => ({ ...prev, whatsapp_api_key: e.target.value }))} className="input-field" placeholder="Enter API key" />
                <p className="text-xs text-olive-400">WhatsApp integration will be configured when API key is provided</p>
              </div>
            )}
            <Button onClick={saveSettings} className="btn-primary gap-2" data-testid="save-integrations-btn"><Save size={16} /> Save</Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* User Dialog */}
      <Dialog open={userDialog} onOpenChange={setUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editUserId ? 'Edit' : 'New'} User</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Name</Label><Input value={userForm.name} onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))} className="input-field" data-testid="user-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">Email</Label><Input type="email" value={userForm.email} onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))} className="input-field" data-testid="user-email-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-semibold text-[#4F734F]">{editUserId ? 'New Password (leave blank to keep)' : 'Password'}</Label><Input type="password" value={userForm.password} onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))} className="input-field" data-testid="user-password-input" /></div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Role</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm(prev => ({ ...prev, role: v }))}>
                <SelectTrigger className="input-field" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="user">User</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={saveUser} className="btn-primary" data-testid="save-user-btn">{editUserId ? 'Update' : 'Create'}</Button>
              <Button variant="outline" onClick={() => setUserDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
